/**
 * JointHeatmap - Shows where both partners have been with overlap highlighting
 */
import { useState, useEffect } from "react";
import { Globe, Heart, Loader2, Lock, User } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { API, useAuth } from "@/App";
import { useEntitlements } from "@/hooks/useEntitlements";
import { PaywallScreen, PaywallType } from "@/components/PaywallScreen";

export default function JointHeatmap() {
  const { token } = useAuth();
  const { hasDuoAccess } = useEntitlements();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaywall, setShowPaywall] = useState(false);

  useEffect(() => {
    fetchHeatmap();
  }, []);

  const fetchHeatmap = async () => {
    try {
      const res = await axios.get(`${API}/premium/duo/heatmap`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (error) {
      console.error("Failed to fetch joint heatmap");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!data?.is_duo) {
    return (
      <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20">
        <CardContent className="p-6 text-center">
          <Globe className="w-10 h-10 text-blue-500 mx-auto mb-3" />
          <h3 className="font-serif text-lg font-semibold mb-2">Joint World Heatmap</h3>
          <p className="text-sm text-muted-foreground">{data?.message || "Link a partner to see your joint map"}</p>
        </CardContent>
      </Card>
    );
  }

  if (!hasDuoAccess) {
    return (
      <>
        <Card className="relative overflow-hidden border-blue-500/20">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-cyan-500/5 backdrop-blur-sm z-10 flex flex-col items-center justify-center">
            <Lock className="w-8 h-8 text-blue-500 mb-2" />
            <p className="text-sm font-medium">Duo Plan Required</p>
            <button onClick={() => setShowPaywall(true)} className="mt-2 px-4 py-1.5 bg-blue-500 text-white text-sm rounded-full">Learn more on duolyst.co.uk</button>
          </div>
          <CardContent className="p-6 blur-sm"><div className="h-32" /></CardContent>
        </Card>
        {showPaywall && <PaywallScreen type={PaywallType.DUO_UPSELL} isOpen={showPaywall} onClose={() => setShowPaywall(false)} />}
      </>
    );
  }

  return (
    <div className="space-y-4" data-testid="joint-heatmap">
      {/* Legend */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-foreground flex items-center gap-2">
              <Globe className="w-4 h-4 text-blue-500" />
              Joint World Map
            </h3>
            <Badge variant="outline" className="text-pink-500 border-pink-500/30">
              {data.overlap_percentage}% overlap
            </Badge>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="p-2 rounded-lg bg-pink-500/10">
              <p className="text-lg font-bold text-pink-500">{data.both.length}</p>
              <p className="text-xs text-muted-foreground">Both visited</p>
            </div>
            <div className="p-2 rounded-lg bg-primary/10">
              <p className="text-lg font-bold text-primary">{data.only_me.length}</p>
              <p className="text-xs text-muted-foreground">Only you</p>
            </div>
            <div className="p-2 rounded-lg bg-purple-500/10">
              <p className="text-lg font-bold text-purple-500">{data.only_partner.length}</p>
              <p className="text-xs text-muted-foreground">Only {data.partner_name?.split(' ')[0]}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Color Legend */}
      <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-pink-500" /> Both</div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-primary" /> You</div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-purple-500" /> {data.partner_name?.split(' ')[0]}</div>
      </div>

      {/* Country Lists */}
      {data.both.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              <Heart className="w-4 h-4 text-pink-500" />
              Visited Together ({data.both.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0">
            <div className="flex flex-wrap gap-1">
              {data.both.map(code => (
                <Badge key={code} variant="outline" className="text-xs bg-pink-500/10 text-pink-500 border-pink-500/30">{code}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
